<template>
  <v-dialog v-model="localDialog" max-width="600" persistent scrollable>
    <v-card class="user-form-dialog">
      <v-card-title class="form-title">
        <v-icon class="mr-2" size="24">{{
          isEditing ? 'mdi-account-edit' : 'mdi-account-plus'
        }}</v-icon>
        {{ isEditing ? 'Editar usuario' : 'Crear nuevo usuario' }}
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <v-form ref="formRef" v-model="formValid" @submit.prevent="handleSubmit">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="formData.name"
                label="Nombre completo"
                :rules="nameRules"
                variant="outlined"
                prepend-inner-icon="mdi-account"
                class="form-field"
                :error-messages="errors.name"
                @input="clearFieldError('name')"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="formData.email"
                label="Correo electrónico"
                :rules="emailRules"
                variant="outlined"
                prepend-inner-icon="mdi-email"
                class="form-field"
                :error-messages="errors.email"
                @input="clearFieldError('email')"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="formData.password"
                :label="isEditing ? 'Nueva contraseña (opcional)' : 'Contraseña *'"
                :rules="isEditing ? passwordRulesOptional : passwordRules"
                :type="showPassword ? 'text' : 'password'"
                variant="outlined"
                prepend-inner-icon="mdi-lock"
                :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append-inner="showPassword = !showPassword"
                class="form-field"
                :error-messages="errors.password"
                @input="clearFieldError('password')"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                v-model="formData.role"
                label="Rol"
                :items="roleOptions"
                :rules="roleRules"
                variant="outlined"
                prepend-inner-icon="mdi-shield-account"
                class="form-field"
                :error-messages="errors.role"
                @update:model-value="clearFieldError('role')"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                v-model="formData.status"
                label="Estado"
                :items="statusOptions"
                :rules="statusRules"
                variant="outlined"
                prepend-inner-icon="mdi-account-check"
                class="form-field"
                :error-messages="errors.status"
                @update:model-value="clearFieldError('status')"
              />
            </v-col>

            <v-col cols="12" v-if="isStudentRole">
              <v-select
                v-model="formData.group_id"
                label="Grupo principal (académico)"
                :items="availableGroups"
                :rules="groupRules"
                variant="outlined"
                prepend-inner-icon="mdi-account-group"
                class="form-field"
                :error-messages="errors.group_id"
                @update:model-value="clearFieldError('group_id')"
                :loading="loadingGroups"
                clearable
                filterable
                :no-data-text="loadingGroups ? 'Cargando grupos...' : 'No hay grupos disponibles'"
                :disabled="loadingGroups"
              />
            </v-col>

            <v-col cols="12" v-if="isStudentRole">
              <v-select
                v-model="formData.group_ids"
                label="Especialidades / Grupos secundarios"
                :items="availableSecondaryGroups"
                variant="outlined"
                prepend-inner-icon="mdi-account-multiple"
                class="form-field"
                :error-messages="errors.group_ids"
                :loading="loadingGroups"
                multiple
                clearable
                filterable
                :no-data-text="loadingGroups ? 'Cargando grupos...' : 'No hay grupos disponibles'"
                :disabled="loadingGroups"
              >
                <template #selection="{ item, index }">
                  <v-chip v-if="index < 3" size="small" class="ma-1">
                    <span>{{ item.title }}</span>
                  </v-chip>
                  <span v-if="index === 3" class="text-body-2 text-medium-emphasis ml-1">
                    (+{{ formData.group_ids.length - 3 }} más)
                  </span>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" v-if="isStudentRole && formData.group_ids.length > 0">
              <v-row dense>
                <v-col
                  v-for="(groupId, index) in formData.group_ids"
                  :key="groupId"
                  cols="12" sm="6"
                >
                  <v-card variant="outlined" class="pa-2 spec-card">
                    <div class="d-flex align-center mb-1">
                      <v-icon size="16" class="mr-1 text-medium-emphasis">mdi-star-outline</v-icon>
                      <span class="text-caption font-weight-medium">
                        {{ getGroupName(groupId) }}
                      </span>
                    </div>
                    <v-select
                      v-model="formData.group_types[index]"
                      :items="membershipTypeOptions"
                      variant="outlined"
                      density="compact"
                      hide-details
                    />
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-alert
            v-if="generalError"
            type="error"
            variant="tonal"
            class="mt-4"
            :text="generalError"
            closable
            @click:close="generalError = ''"
          />
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-6">
        <v-spacer></v-spacer>

        <v-btn variant="outlined" @click="cancel" :disabled="loading" class="cancel-btn mr-2">
          Cancelar
        </v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          @click="handleSubmit"
          :disabled="!formValid"
          class="submit-btn"
        >
          {{ isEditing ? 'Actualizar usuario' : 'Crear usuario' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted } from 'vue'
import { useStore } from 'vuex'

const props = defineProps({
  dialog: {
    type: Boolean,
    default: false,
  },
  user: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:dialog', 'submit', 'cancel'])

const store = useStore()
const formRef = ref(null)
const formValid = ref(false)
const showPassword = ref(false)
const generalError = ref('')
const loadingGroups = ref(false)

const formData = reactive({
  name: '',
  email: '',
  password: '',
  role: '',
  status: true,
  group_id: null,
  group_ids: [],
  group_types: [],
})

const errors = reactive({
  name: [],
  email: [],
  password: [],
  role: [],
  status: [],
  group_id: [],
  group_ids: [],
  group_types: [],
})

const roleOptions = [
  { title: 'Administrador', value: 'admin' },
  { title: 'Docente', value: 'teacher' },
  { title: 'Estudiante', value: 'student' },
]

const statusOptions = [
  { title: 'Activo', value: true },
  { title: 'Inactivo', value: false },
]

const nameRules = [
  (v) => !!v || 'El nombre es requerido',
  (v) => (v && v.length >= 2) || 'El nombre debe tener al menos 2 caracteres',
  (v) => (v && v.length <= 100) || 'El nombre no debe exceder 100 caracteres',
]

const emailRules = [
  (v) => !!v || 'El correo electrónico es requerido',
  (v) => /.+@.+\..+/.test(v) || 'El correo electrónico debe ser válido',
]

const passwordRules = [
  (v) => !!v || 'La contraseña es requerida',
  (v) => (v && v.length >= 8) || 'La contraseña debe tener al menos 8 caracteres',
  (v) => {
    if (!v) return true
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/
    return regex.test(v) || 'La contraseña debe contener al menos una mayúscula, una minúscula, un número y un carácter especial (@$!%*?&)'
  }
]

const passwordRulesOptional = [
  (v) => {
    if (!v || v.length === 0) return true
    return v.length >= 8 || 'La contraseña debe tener al menos 8 caracteres'
  },
  (v) => {
    if (!v || v.length === 0) return true
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/
    return regex.test(v) || 'La contraseña debe contener al menos una mayúscula, una minúscula, un número y un carácter especial (@$!%*?&)'
  }
]

const roleRules = [(v) => !!v || 'El rol es requerido']

const statusRules = [(v) => (v !== null && v !== undefined) || 'El estado es requerido']

const groupRules = [
  (v) => {
    if (formData.role === 'student') {
      return !!v || 'El grupo es requerido para estudiantes'
    }
    return true
  }
]

const isEditing = computed(() => !!props.user?.id)

const isStudentRole = computed(() => formData.role === 'student')

const availableGroups = computed(() => {
  const groups = store.getters['groups/activeGroups'] || []
  return groups.map(group => ({
    title: group.name,
    value: group.id
  }))
})

const availableSecondaryGroups = computed(() => {
  return availableGroups.value.filter(g => g.value !== formData.group_id)
})

const membershipTypeOptions = [
  { title: 'Especialidad', value: 'specialty' },
  { title: 'Secundario', value: 'secondary' },
]

const getGroupName = (groupId) => {
  const group = availableGroups.value.find(g => g.value === groupId)
  return group ? group.title : 'Grupo desconocido'
}

const localDialog = computed({
  get: () => props.dialog,
  set: (value) => emit('update:dialog', value),
})

// Función para cargar todos los grupos (sin paginación) para los selects
const ensureGroupsLoaded = async () => {
  loadingGroups.value = true
  try {
    await store.dispatch('groups/fetchGroups', { all: true })
  } finally {
    loadingGroups.value = false
  }
}

watch(
  () => props.dialog,
  async (newVal) => {
    if (newVal) {
      resetForm()

      // Cargar grupos de forma asíncrona
      const groupsPromise = ensureGroupsLoaded()

      // Poblar formulario inmediatamente si hay usuario
      if (props.user) {
        populateForm(props.user)
      }

      // Esperar a que se carguen los grupos antes de continuar
      await groupsPromise

      // Si es un estudiante y se cargaron los grupos después del populate, repoblar
      if (props.user && props.user.role === 'student' && (props.user.group_id || props.user.group)) {
        await nextTick(() => {
          populateForm(props.user)
        })
      }

      nextTick(() => {
        if (formRef.value) {
          formRef.value.resetValidation()
        }
      })
    }
  },
)

// Clear group_id when role changes from student to another role
watch(
  () => formData.role,
  async (newRole, oldRole) => {
    if (oldRole === 'student' && newRole !== 'student') {
      formData.group_id = null
      formData.group_ids = []
      formData.group_types = []
      clearFieldError('group_id')
      clearFieldError('group_ids')
    }
    // Cargar grupos cuando se selecciona rol estudiante
    if (newRole === 'student') {
      await ensureGroupsLoaded()
    }
  }
)

// Al cambiar grupos secundarios, sincronizar group_types y default 'specialty'
watch(
  () => formData.group_ids,
  (newIds) => {
    while (formData.group_types.length < newIds.length) {
      formData.group_types.push('specialty')
    }
    if (formData.group_types.length > newIds.length) {
      formData.group_types.splice(newIds.length)
    }
  },
  { deep: true }
)

// Cargar grupos al montar el componente
onMounted(async () => {
  await ensureGroupsLoaded()
})

const populateForm = (user) => {
  formData.name = user.name || ''
  formData.email = user.email || ''
  formData.password = ''
  formData.role = user.role || ''
  formData.status = user.is_active !== undefined ? user.is_active : true

  // Handle group_id from different possible structures
  let groupId = null
  if (user.group_id) {
    groupId = user.group_id
  } else if (user.group && user.group.id) {
    groupId = user.group.id
  }

  // Solo asignar si es estudiante y existe el grupo en las opciones disponibles
  if (user.role === 'student' && groupId) {
    const groupExists = availableGroups.value.some(group => group.value === groupId)
    if (groupExists) {
      formData.group_id = groupId
    } else {
      formData.group_id = null
    }
  } else {
    formData.group_id = null
  }

  // Handle secondary groups from user.groups array
  formData.group_ids = []
  formData.group_types = []
  if (user.role === 'student' && Array.isArray(user.groups)) {
    const nonPrimary = user.groups.filter(g => g.type !== 'primary')
    const availableIds = availableGroups.value.map(g => g.value)
    nonPrimary.forEach(g => {
      if (availableIds.includes(g.id)) {
        formData.group_ids.push(g.id)
        formData.group_types.push(g.type || 'secondary')
      }
    })
  }
}

const resetForm = () => {
  formData.name = ''
  formData.email = ''
  formData.password = ''
  formData.role = ''
  formData.status = true
  formData.group_id = null
  formData.group_ids = []
  formData.group_types = []

  Object.keys(errors).forEach((key) => {
    errors[key] = []
  })

  generalError.value = ''
  showPassword.value = false
}

const clearFieldError = (field) => {
  errors[field] = []
  generalError.value = ''
}

const handleSubmit = async () => {
  if (!formRef.value) return

  const { valid } = await formRef.value.validate()
  if (!valid) return

  const submitData = {
    name: formData.name.trim(),
    email: formData.email.trim(),
    role: formData.role,
    is_active: formData.status,
  }

  // Include password only if it's filled
  if (formData.password && formData.password.trim().length > 0) {
    submitData.password = formData.password
  }

  // Include group_id only for students
  if (formData.role === 'student' && formData.group_id) {
    submitData.group_id = formData.group_id
  }

  // Include secondary groups only for students
  if (formData.role === 'student' && formData.group_ids.length > 0) {
    submitData.group_ids = formData.group_ids
    submitData.group_types = formData.group_types.map(t => t || 'secondary')
  }

  emit('submit', submitData)
}

const cancel = () => {
  emit('cancel')
  localDialog.value = false
}

const setFieldErrors = (fieldErrors) => {
  Object.keys(fieldErrors).forEach((field) => {
    if (errors.hasOwnProperty(field)) {
      errors[field] = Array.isArray(fieldErrors[field]) ? fieldErrors[field] : [fieldErrors[field]]
    }
  })
}

const setGeneralError = (error) => {
  generalError.value = error
}

defineExpose({
  setFieldErrors,
  setGeneralError,
  resetForm,
})
</script>

<style scoped>
.user-form-dialog {
  border-radius: 16px !important;
  background: #ffffff;
  box-shadow: 0 8px 32px rgba(10, 25, 47, 0.15);
}

.form-title {
  background: linear-gradient(135deg, #0a192f 0%, #112240 100%);
  color: #ffffff !important;
  padding: 20px 24px;
  font-weight: 600;
  font-size: 1.25rem;
  border-radius: 16px 16px 0 0;
}

.form-field :deep(.v-field) {
  border-radius: 12px;
  background: #fafbfc;
}

.form-field :deep(.v-field--focused) {
  background: #ffffff;
}

.form-field :deep(.v-field__outline) {
  --v-field-border-opacity: 0.3;
}

.form-field :deep(.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 1;
  --v-field-border-width: 2px;
  color: #0a192f;
}

.form-field :deep(.v-field__prepend-inner .v-icon) {
  color: #64748b;
  margin-right: 8px;
}

.form-field :deep(.v-field--focused .v-field__prepend-inner .v-icon) {
  color: #0a192f;
}

.form-field :deep(.v-label) {
  color: #64748b;
  font-weight: 500;
}

.form-field :deep(.v-field--focused .v-label) {
  color: #0a192f;
}

.cancel-btn {
  border-color: #cbd5e1 !important;
  color: #64748b !important;
  text-transform: none;
  font-weight: 500;
  border-radius: 10px;
  padding: 0 24px;
  height: 44px;
}

.cancel-btn:hover {
  background-color: #f1f5f9 !important;
  border-color: #94a3b8 !important;
}

.submit-btn {
  background: linear-gradient(135deg, #0a192f 0%, #112240 100%) !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
  border-radius: 10px;
  padding: 0 24px;
  height: 44px;
  box-shadow: 0 4px 12px rgba(10, 25, 47, 0.3);
}

.submit-btn:hover {
  box-shadow: 0 6px 16px rgba(10, 25, 47, 0.4);
  transform: translateY(-1px);
}

.submit-btn:disabled {
  opacity: 0.6;
  transform: none !important;
  box-shadow: 0 2px 8px rgba(10, 25, 47, 0.2) !important;
}

.spec-card {
  border-radius: 8px;
  background: #f8fafc;
}

@media (max-width: 600px) {
  .user-form-dialog {
    margin: 16px;
    border-radius: 12px !important;
  }

  .form-title {
    padding: 16px 20px;
    font-size: 1.1rem;
    border-radius: 12px 12px 0 0;
  }

  .v-card-text {
    padding: 20px !important;
  }

  .v-card-actions {
    padding: 16px 20px !important;
    flex-direction: column;
    gap: 12px;
  }

  .cancel-btn,
  .submit-btn {
    width: 100%;
    margin: 0 !important;
  }
}
</style>
